import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  Avatar,
  Typography,
  Chip,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import { Person } from '@mui/icons-material';
import { useUpdateUser } from '../hooks/useUsers';
import UserRoleAssignment from './UserRoleAssignment';
import type { ManagedUser } from '../api/types';
import { DraggableDialog } from './DraggableDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

interface EditUserDialogProps {
  open: boolean;
  onClose: () => void;
  user: ManagedUser | null;
  onSuccess?: () => void;
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({
  open,
  onClose,
  user,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'viewer' as 'admin' | 'editor' | 'viewer',
    status: 'active' as 'pending' | 'active' | 'disabled',
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const updateMutation = useUpdateUser();

  // Reset form when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: (user as any).phone || '',
        role: (user.role as 'admin' | 'editor' | 'viewer') || 'viewer',
        status: user.status || 'active',
      });
      setSubmitError(null);
    }
  }, [user]);

  const handleClose = () => {
    setSubmitError(null);
    setActiveTab(0);
    onClose();
  };

  const handleSubmit = async () => {
    if (!user) return;
    setSubmitError(null);

    // Validate name
    if (!formData.name.trim()) {
      setSubmitError('Name is required');
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: user.id,
        data: {
          name: formData.name.trim(),
          phone: formData.phone.trim() || undefined,
          role: formData.role,
          status: formData.status,
        },
      });

      onSuccess?.();
      handleClose();
    } catch (err: any) {
      console.error('Update user error:', err);
      setSubmitError(err?.response?.data?.error || err?.message || 'Failed to update user');
    }
  };

  if (!user) return null;

  const isGlobalAdmin = user.global_role === 'global_admin';

  const actionsContent = (
    <>
      <Button onClick={handleClose}>Cancel</Button>
      {activeTab === 0 && (
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={updateMutation.isPending}
          sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
        >
          {updateMutation.isPending ? (
            <CircularProgress size={20} sx={{ color: 'white' }} />
          ) : (
            'Save Changes'
          )}
        </Button>
      )}
    </>
  );

  return (
    <DraggableDialog
      open={open}
      onClose={handleClose}
      title="Edit User"
      maxWidth="md"
      actions={actionsContent}
    >
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      {/* User Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, mt: 1 }}>
        <Avatar sx={{ width: 56, height: 56, bgcolor: '#10b981' }}>
          {user.image ? (
            <img src={user.image} alt={user.name} style={{ width: '100%', height: '100%' }} />
          ) : (
            <Person sx={{ fontSize: 32 }} />
          )}
        </Avatar>
        <Box>
          <Typography variant="h6">{user.name || 'Unnamed User'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
          {isGlobalAdmin && (
            <Chip
              label="Global Admin"
              size="small"
              sx={{ mt: 0.5, bgcolor: '#ef4444', color: 'white' }}
            />
          )}
        </Box>
      </Box>

      <Divider />

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Details" />
        <Tab label="Roles & Permissions" />
      </Tabs>

      {/* Details Tab */}
      <TabPanel value={activeTab} index={0}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Full Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            required
            placeholder="John Doe"
          />

          <TextField
            label="Email Address"
            value={formData.email}
            disabled
            fullWidth
            helperText="Email cannot be changed"
          />

          <TextField
            label="Phone Number"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            fullWidth
            placeholder="+1 (555) 123-4567"
            helperText="Optional contact number"
          />

          <FormControl fullWidth>
            <InputLabel>Legacy Role</InputLabel>
            <Select
              value={formData.role}
              label="Legacy Role"
              onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
              disabled={isGlobalAdmin}
            >
              <MenuItem value="admin">Admin - Full organization access</MenuItem>
              <MenuItem value="editor">Editor - Can manage content and devices</MenuItem>
              <MenuItem value="viewer">Viewer - Read-only access</MenuItem>
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Use the Roles tab for granular RBAC permissions
            </Typography>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              label="Status"
              onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
            >
              <MenuItem value="active">Active - Can log in normally</MenuItem>
              <MenuItem value="disabled">Disabled - Cannot log in</MenuItem>
              {user.status === 'pending' && (
                <MenuItem value="pending">Pending - Awaiting invite acceptance</MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Organization Memberships (read-only display) */}
          {user.organizations && user.organizations.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Organization Memberships
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {user.organizations.map((org) => (
                  <Chip
                    key={org.id}
                    label={`${org.name} (${org.role})`}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </TabPanel>

      {/* Roles Tab */}
      <TabPanel value={activeTab} index={1}>
        <UserRoleAssignment
          userId={user.id}
          userName={user.name}
          isReadOnly={false}
        />
      </TabPanel>
    </DraggableDialog>
  );
};

export default EditUserDialog;
